import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import RowActions, { type RowAction } from '../../../components/RowActions';
import type { FaqItem } from '../types';
import { FAQ_PAGE_LABELS as LABELS } from '../../../utils/labels';

interface FaqItemAccordionProps {
  item: FaqItem;
  onEdit?: () => void;
  onDelete?: () => void;
  defaultExpanded?: boolean;
}

export default function FaqItemAccordion({ item, onEdit, onDelete, defaultExpanded = false }: FaqItemAccordionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const actions: RowAction[] = [
    ...(onEdit
      ? [{ label: LABELS.editLabel, icon: <EditIcon fontSize="small" />, onClick: onEdit, color: 'primary' as const, testId: 'edit-button' }]
      : []),
    ...(onDelete
      ? [{ label: LABELS.deleteLabel, icon: <DeleteOutlineIcon fontSize="small" />, onClick: onDelete, color: 'error' as const, testId: 'delete-button' }]
      : []),
  ];

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      className="border border-border rounded-xl mb-2"
      sx={{ '&:before': { display: 'none' } }}
      data-testid={`faq-item-${item.id}`}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <p className={`font-medium mb-0 mt-0 flex-1 text-base ${expanded ? 'text-primary' : ''}`}>{item.question}</p>
          {actions.length > 0 && (
            <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 shrink-0">
              <RowActions actions={actions} />
            </div>
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <p className="whitespace-pre-line text-text-secondary mb-0 mt-0 text-base">{item.answer}</p>
      </AccordionDetails>
    </Accordion>
  );
}
